import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DispatchRequestEntity, dispatchRequestStatus} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";

export type DispatchRequestAction = "COMMENT" | "CANCEL" | "REASSIGN" | "EDIT" | "AUTH"

export type DispatchRequestActionMenuDropdownProps = {
  dispatchRequest: DispatchRequestEntity
  onActionClick?: (action: DispatchRequestAction) => void
  showEmpty?: boolean
}

export const DispatchRequestActionMenuDropdown = (props: DispatchRequestActionMenuDropdownProps) => {
  const {dispatchRequest, onActionClick, showEmpty = true} = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<DispatchRequestAction> = useMemo(() => {
    const actions = new Set<DispatchRequestAction>()
    if (hasAccess("dispatchrequest_edit")) {
      actions.add("COMMENT")

      if (!dispatchRequest.reassignmentImminent) {
        switch (dispatchRequest.status) {
          case dispatchRequestStatus.pending:
            actions.add("EDIT")
            actions.add("CANCEL")

            if (!dispatchRequest.isAuthorized) {
              actions.add("AUTH")
            }
            break
          case dispatchRequestStatus.accepted:
            actions.add("EDIT")
            actions.add("CANCEL")
            break
          case dispatchRequestStatus.rejected:
            actions.add("REASSIGN")
            break
          default:
            break
        }
      }
    }

    return actions
  }, [dispatchRequest, hasAccess])

  const onAddCommentClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('COMMENT')
    }
  }, [onActionClick])

  const onCancelClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('CANCEL')
    }
  }, [onActionClick])

  const onReassignClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('REASSIGN')
    }
  }, [onActionClick])

  const onEditClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('EDIT')
    }
  }, [onActionClick])

  const onAuthClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('AUTH')
    }
  }, [onActionClick])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("COMMENT") &&
        <Menu.Item
          onClick={onAddCommentClick}>
          {t("common.action.comment.add", "Add Comment")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item
          onClick={onEditClick}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("AUTH") &&
        <Menu.Item
          onClick={onAuthClick}>
          {t("common.action.authorize", "Authorize")}
        </Menu.Item>
      }
      {(visibleActions.has("CANCEL") || visibleActions.has("REASSIGN")) &&
        <Menu.Divider/>
      }
      {visibleActions.has("CANCEL") &&
        <Menu.Item
          color="red"
          onClick={onCancelClick}>
          {t("common.action.cancel", "Cancel")}
        </Menu.Item>
      }
      {visibleActions.has("REASSIGN") &&
        <Menu.Item
          color="red"
          onClick={onReassignClick}>
          {t("common.action.reassign", "Reassign")}
        </Menu.Item>
      }
    </>
  )
}
